import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, IconButton, Box, Typography,
  CircularProgress, Avatar, Chip, Stack, Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useConversation } from '../hooks/useConversation';
import { api } from '../api';
import { useTheme, alpha } from '@mui/material/styles';

export default function ConversationModal({
  open,
  onClose,
  userId,
  partnerId,
  title,
  userRole = 'recipient',
  partnerRole = 'bidder'
}) {
  const { loading, messages, threadKey, error } = useConversation(userId, partnerId);
  const theme = useTheme();

  const [uMeta, setUMeta] = React.useState(null);      // { username, avatarUrl, role }
  const [pMeta, setPMeta] = React.useState(null);      // { username, avatarUrl, role }
  const [metaLoading, setMetaLoading] = React.useState(true);

  // Build header using fetched usernames once available
  const header = React.useMemo(() => {
    if (uMeta && pMeta) {
      return `Conversation — ${uMeta.username} ↔ ${pMeta.username}`;
    }
    return title || `Conversation — ${userId} ↔ ${partnerId}`;
  }, [uMeta, pMeta, title, userId, partnerId]);

  // Helpers
  const asNum = (v) => (v == null ? NaN : Number(v));
  const whoSent = (m) => {
    const sid = asNum(m.senderId ?? m.sender_id ?? m.userId ?? m.fromUserId);
    if (sid === asNum(userId)) return 'user';
    if (sid === asNum(partnerId)) return 'partner';
    return 'system';
  };
  const tsOf = (m) => {
    const t = (typeof m.ts === 'number' ? m.ts : (typeof m.timestamp === 'number' ? m.timestamp : 0));
    return t ? new Date(t) : null;
  };
  const fmt = (d) => (d ? d.toLocaleString() : '');

  // Bubble styling by ROLE (not viewer)
  const bubbleSx = (side) => {
    const role = side === 'user' ? userRole : partnerRole;
    if (role === 'bidder') {
      return {
        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.35 : 0.12),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`
      };
    }
    // recipient
    return {
      bgcolor: theme.palette.action.hover,
      border: `1px solid ${theme.palette.divider}`
    };
  };

  // Fetch display names + avatars for both participants
  React.useEffect(() => {
    let mounted = true;
    async function loadMeta(id, role) {
      try {
        const [detailResult, imgResult] = await Promise.allSettled([
          api.get(`/users/${id}`),
          api.get(`/secure-image-url/${id}`)
        ]);
        
        // Extract username from the response structure
        let username = `User #${id}`;
        if (detailResult.status === 'fulfilled' && detailResult.value?.data) {
          const data = detailResult.value.data;
          username = data.user?.username || data.username || `User #${id}`;
        }
        
        // Extract avatar URL (get first image from urls array)
        let avatarUrl = null;
        if (imgResult.status === 'fulfilled' && imgResult.value?.data) {
          // API returns { urls: [...] } or { url: '...' }
          if (imgResult.value.data.urls && Array.isArray(imgResult.value.data.urls) && imgResult.value.data.urls.length > 0) {
            avatarUrl = imgResult.value.data.urls[0];
          } else if (imgResult.value.data.url) {
            avatarUrl = imgResult.value.data.url;
          }
        }
        
        return { id, username, avatarUrl, role };
      } catch (err) {
        console.error(`Error loading metadata for user ${id}:`, err);
        return { id, username: `User #${id}`, avatarUrl: null, role };
      }
    }
    (async () => {
      setMetaLoading(true);
      const [u, p] = await Promise.all([
        loadMeta(userId, userRole),
        loadMeta(partnerId, partnerRole)
      ]);
      if (!mounted) return;
      setUMeta(u); setPMeta(p);
      setMetaLoading(false);
    })();
    return () => { mounted = false; };
  }, [userId, partnerId, userRole, partnerRole]);

  if (!open) return null;

  const legend = (
    <Stack direction="row" spacing={1} sx={{ mt: .75 }}>
      {uMeta && (
        <Chip
          size="small"
          avatar={<Avatar src={uMeta.avatarUrl || undefined}>{uMeta.username?.[0]?.toUpperCase?.() || 'U'}</Avatar>}
          label={`${uMeta.username} — ${uMeta.role}`}
          variant="outlined"
          sx={{ borderColor: alpha(theme.palette.text.primary, 0.2) }}
        />
      )}
      {pMeta && (
        <Chip
          size="small"
          avatar={<Avatar src={pMeta.avatarUrl || undefined}>{pMeta.username?.[0]?.toUpperCase?.() || 'U'}</Avatar>}
          label={`${pMeta.username} — ${pMeta.role}`}
          variant="outlined"
          sx={{ borderColor: alpha(theme.palette.text.primary, 0.2) }}
        />
      )}
    </Stack>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ pr: 6 }}>
        {header} {threadKey ? `(${threadKey})` : ''}
        {legend}
        <IconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: 'background.default' }}>
        {loading || metaLoading ? (
          <Box sx={{ display:'flex', justifyContent:'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography variant="body2" color="error">{error}</Typography>
        ) : !messages.length ? (
          <Typography variant="body2" color="text.secondary">No messages yet.</Typography>
        ) : (
          <Box sx={{ display:'flex', flexDirection:'column', gap: 1.25 }}>
            {messages.map((m) => {
              const side = whoSent(m); // 'user' | 'partner' | 'system'
              const meta = side === 'user' ? uMeta : (side === 'partner' ? pMeta : null);
              const hasBlob = m.blobUrl && String(m.blobUrl).length > 4;
              const t = tsOf(m);

              if (side === 'system') {
                // Neutral system-ish line (missing senderId etc.)
                return (
                  <Box key={m.id} sx={{ alignSelf: 'center', opacity: 0.7 }}>
                    <Typography variant="caption">{meta?.username || 'System'} · {fmt(t)}</Typography>
                  </Box>
                );
              }

              const alignRight = (side === 'user'); // recipient on the right, bidder on the left

              return (
                <Box
                  key={m.id}
                  sx={{
                    display: 'flex',
                    justifyContent: alignRight ? 'flex-end' : 'flex-start',
                    gap: 1
                  }}
                >
                  {!alignRight && (
                    <Tooltip title={`${meta?.username} — ${meta?.role || ''}`}>
                      <Avatar
                        src={meta?.avatarUrl || undefined}
                        alt={meta?.username}
                        sx={{ width: 32, height: 32, mt: .25 }}
                      >
                        {(meta?.username || 'U').charAt(0).toUpperCase()}
                      </Avatar>
                    </Tooltip>
                  )}

                  <Box
                    sx={{
                      maxWidth: '78%',
                      px: 1.25,
                      py: 1,
                      borderRadius: 2,
                      boxShadow: 1,
                      ...bubbleSx(side),
                    }}
                  >
                    <Typography variant="caption" sx={{ display:'block', mb: .25, opacity: .8 }}>
                      {meta?.username} · {meta?.role} • {fmt(t)}{m.status ? ` · ${m.status}` : ''}
                    </Typography>
                    {hasBlob && (
                      <Typography variant="body2" sx={{ mb: .5 }}>
                        <a href={m.blobUrl} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                          Attachment
                        </a>
                      </Typography>
                    )}
                    {m.text && (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {m.text}
                      </Typography>
                    )}
                  </Box>

                  {alignRight && (
                    <Tooltip title={`${meta?.username} — ${meta?.role || ''}`}>
                      <Avatar
                        src={meta?.avatarUrl || undefined}
                        alt={meta?.username}
                        sx={{ width: 32, height: 32, mt: .25 }}
                      >
                        {(meta?.username || 'U').charAt(0).toUpperCase()}
                      </Avatar>
                    </Tooltip>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
